import { Inject } from '@odinjs/odin';
import { BarcodeAPI, BarcodeData, ButtonAPI, DeckAPI, DeckValue, FormAPI, ModalService, TextAPI, ToastService, PanelAPI } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnClick, OnMount, OnReadSuccess, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import { ClickEvent, MountEvent, ReadEvent, ValidateEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { isEmptyOrBlankString } from '@philips-internal/oswald-ui-utils';
import moment from 'moment';
import { MilkAndDerivativesExecuteAliases as Aliases, FragmentNames } from '../../../../codegen';
import { ApplicationRoute } from '../../../../constants/routes';
import { BarcodeUtils } from '../../../../utils/barcode-utils';
import { FoundationUtils } from '../../../../utils/foundation-utils';
import { ADEP_E1113Parameters, ADEP_E9129Parameters, FunctionName } from '../../../../utils/parameters';
import BaseController from '../../../BaseController';
import MedicationService, { ConsistAdministrationMarginInfoRequest, CreatePrescriptionScheduleChangeRequest, CreatePrescriptionScheduleChangeResponse } from '../../services/medication-service';
import TimelineContext, { TimeLineEventMedication } from '../../services/timeline-service';
import { validateBarcode } from './milk-and-derivatives-service';
import BarcodeService from '../../../../services/barcode-service';
import { ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import { Function1113Param13Values } from '../oral-supplements-viewer/oral-supplements-params';
import { YesNoType } from '../../../../utils/enum-utils';

const FUNCTION_1113 = 'ADEP#1113';

@Controller({ fragment: FragmentNames.MILK_AND_DERIVATIVES_EXECUTE })
class MilkAndDerivativesExecuteController extends BaseController {

  @Inject(Aliases.MILK_AND_DERIVATIVES_INFORMATION_DECK)
  private deckOfCardsApi!: DeckAPI;

  @Inject(Aliases.INFORMATION_FORM)
  private formApi!: FormAPI;

  @Inject(Aliases.BUTTON_EXECUTE)
  private executeButtonApi!: ButtonAPI;

  @Inject(Aliases.PANEL_BARCODE)
  private readonly barcodePanelAPI!: PanelAPI;

  @OnMount(Aliases.MILK_AND_DERIVATIVES_INFORMATION_DECK)
  onMountDeck(event: MountEvent): void {
    const deck = event.target as DeckAPI;
    const { materialDescription, doseQuantity, read, materialObservation, unitMeasureDoseDescription, barcode } = this.context.getBulkData();
    const deckValue = {
      barcode,
      doseQuantity,
      materialDescription,
      observation: materialObservation,
      quantityDispensed: doseQuantity,
      read,
      unitMeasureCode: unitMeasureDoseDescription,
    } as DeckValue;
    deck.setData([ deckValue ]);
  }

  @OnMount(Aliases.INFORMATION_FORM)
  onMountForm(event: MountEvent): void {
    const form = event.target as FormAPI;
    const { doseQuantity, dilutionDescription, unitMeasureDoseCode } = this.context.getBulkData() as Record<string, string>;
    form.setData({
      amountAdministered: doseQuantity,
      date: moment().utc().format(ISO_FULL_DATE_FORMAT),
      dilutionDescription,
      doseQuantity: `${doseQuantity} ${unitMeasureDoseCode}` ,
    } as Record<string, Value>);
  }

  @OnMount([Aliases.TEXT_MEDICATION_DATE, Aliases.TEXT_MEDICATION_INFORMATION])
  onMountTexts({ target }: EventData<TextAPI>): void {
    const { materialDescription, scheduleDate } = this.context.getBulkData();
    const value = target.getValue()
      .replace(':medication-information', materialDescription as string)
      .replace(':medication-date', moment(scheduleDate as string).format('L LTS'));

    target.setValue(value);
  }

  @OnValidate(Aliases.INFORMATION_FORM)
  onChangeForm(event: ValidateEvent): void {
    const isFormValid = event.valid;
    this.context.set({ valid: isFormValid });
  }

  @OnMount(Aliases.BUTTON_EXECUTE)
  handleMountButtonExecute(): void {
    const isBarcodeValid = this.context.get('barcodeValid') as boolean;
    this.executeButtonApi.setEnabled(!!(this.context.get('valid') && isBarcodeValid));
  }

  @OnMount(Aliases.BARCODE_MILK_AND_DERIVATIVES)
  handleMountBarcode(event: MountEvent): void {
    const barcodeApi = event.target as BarcodeAPI;
    barcodeApi.setDefaultReadType(BarcodeService.getDefaultMethodOfReadingBarcode());

    const checkMedicationBeforeAdmin = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.CHECK_MEDICATIONS_BEFORE_ADMIN);
    
    if (checkMedicationBeforeAdmin === YesNoType.NO) {
      this.barcodePanelAPI.setVisible(false);
      this.context.set({ barcodeValid: true });
    } else {
      this.barcodePanelAPI.setVisible(true);
      this.context.set({ barcodeValid: false });
    }
  }

  @OnReadSuccess(Aliases.BARCODE_MILK_AND_DERIVATIVES)
  onBarcodeRead(event: ReadEvent<BarcodeData>): void {
    const barcodeApi = event.target as BarcodeAPI;
    barcodeApi.close();

    const readBarcode = event.item.value.trim();
    const barcode = this.context.get('barcode');
    const isFormValid = this.context.get('valid') as boolean;
    const isValid = validateBarcode(readBarcode, barcode);
    if (isValid) {
      this.changeDeckReadState(true);
      isFormValid && this.executeButtonApi.setEnabled(true);
    } else {
      this.showToast('text.invalid-manipulation-order', ToastLevel.WARNING, ToastType.BANNER);
    }
  }

  @OnClick(Aliases.BUTTON_EXECUTE)
  onClickInExecute(): void {
    const param13 = FoundationUtils.findModuleParam(FUNCTION_1113, ADEP_E1113Parameters.ACTION_TO_BE_TAKEN_IN_CASE_OF_EXTRAPOLATION_OF_THE_CHECKING_MARGIN) as string;
    const param507 = FoundationUtils.findModuleParam(FUNCTION_1113, ADEP_E1113Parameters.ACTION_IN_CASE_EXTRAPOLATION_CHECKING_MARGIN_PRIOR_START) as number;
    const param12 = FoundationUtils.findModuleParam(FUNCTION_1113, ADEP_E1113Parameters.MARGIN_IN_MINUTES_CHECKING_OUTSIDE_PRESCRIBED_HOURS) as number;
    const justificationRequired = param13 === Function1113Param13Values.REQUIRED_JUSTIFICATION || param13 === Function1113Param13Values.NOT_ALLOWED_AND_jUSTIFICATION;
    const requiresJustificationStatus =  justificationRequired ? YesNoType.YES : YesNoType.NO;

    const { scheduleSequenceNumber } = this.historyService.getCurrentRouteParams();
    const { justification, date } = this.formApi.getRecord();
    const eventDate = moment(date as string).format('YYYY-MM-DDTHH:mm:ss[Z]');

    if (!this.validateAdministeredDose()) {
      return;
    }
    const body: ConsistAdministrationMarginInfoRequest = {
      actionAdministrationOutsideMarginStatus: param13,
      eventDate,
      hasAsEvent: justification ? 'S' : 'N',
      itemType: 'LD',
      justificationDescription: justification ? String(justification) : '',
      minutesMarginBefore: param507 ?? 0,
      minutesQuantityMarginAdministration: param12,
      requiresJustificationStatus: requiresJustificationStatus,
      scheduleSequence: Number(scheduleSequenceNumber),
    };

    this.validateDoseExtrapolationParam()
      .then(isValidated => {
        if (isValidated) {
          return MedicationService.consistAdministrationMarginInfoResponse(body)
            .then((response) => {
              if (response.responseDescription) {
                this.showToast(response.responseDescription, ToastLevel.WARNING, ToastType.BANNER);
                return Promise.reject(false);
              }
              return this.createPrescription()
            })
        }
        return this.createPrescription();
      })
      .then((response) => {
        this.showToast(response.messageDescriptionResponse || 'tws.emar.administered-item', ToastLevel.SUCCESS, ToastType.BANNER);
        this.isLoading(false);
        this.redirectUserToTimeline();
      })
      .catch(reason => {
        const message = reason?.body?.errors.join(', ') || reason || ''
        this.isLoading(false);
        this.showError(message);
        !!reason && this.redirectUserToTimeline();
      })
  }

  @OnClick(Aliases.ADMINISTRATION_BY_PROCESS_CARD)
  onDisaposeClickEvent(event: ClickEvent): void {
    const barcode = event.value.barcode;

    if (barcode) {
      BarcodeUtils.dialogModalConfirm(barcode, () => this.changeDeckReadState(false));
    }
  }

  private validateDoseExtrapolationParam(): Promise<boolean> {
    const param13 = FoundationUtils.findModuleParam(FUNCTION_1113, ADEP_E1113Parameters.ACTION_TO_BE_TAKEN_IN_CASE_OF_EXTRAPOLATION_OF_THE_CHECKING_MARGIN) as string;
    return Promise.resolve(!['NC', 'N'].includes(param13))
  }
  private createPrescription(): Promise<CreatePrescriptionScheduleChangeResponse> {
    const { encounterId, scheduleSequenceNumber } = this.historyService.getCurrentRouteParams();
    const timelineMedication = TimelineContext.get() as TimeLineEventMedication;
    const param22 = FoundationUtils.findModuleParam(FUNCTION_1113, ADEP_E1113Parameters.PRESCRIPTION_HOUR_QUANTITY) as number;
    const { grouppingNumber, unitMeasureDoseCode, intervalCode, prescriptionNumber, materialSequence, doseQuantity } = this.context.getBulkData();
    const { justification, observation, amountAdministered, date } = this.formApi.getRecord();
    const eventDate = moment(date as string).format('YYYY-MM-DDTHH:mm:ss[Z]');

    const body: CreatePrescriptionScheduleChangeRequest = {
      administrationRouteType: '',
      componentsDescription: '',
      encounterNumber: Number(encounterId),
      eventDate,
      functionCode: 9129,
      groupingNumber: Number(grouppingNumber),
      intervalCode: String(intervalCode),
      isGenerateAll: '',
      itemCode: String(timelineMedication.itemCode),
      itemDescription: String(timelineMedication.description),
      itemObservationDescription: '',
      itemQuantity: Number(doseQuantity),
      itemSequence: Number(materialSequence),
      itemType: 'LD',
      justifyDescription: justification ? String(justification) : '',
      messageDescriptionResponse: '',
      observationDescription: observation ? String(observation) : '',
      pdoPrn: 'N',
      prescriptionHourQuantity: param22,
      prescriptionNumber: Number(prescriptionNumber),
      prescriptionsNumber: String(prescriptionNumber),
      quantityAdministered: Number(amountAdministered),
      scheduleSequence: Number(scheduleSequenceNumber),
      suspendPrescribedMaterial: '',
      unitMeasureCode: String(unitMeasureDoseCode),
      uoMCodeQuantityAdministered: String(unitMeasureDoseCode),
    }
    return MedicationService.createPrescriptionScheduleChange(body)
  }

  private showError(msg: string): void {
    msg && this.showToast('', ToastLevel.WARNING, ToastType.BUBBLE);
  }

  private validateAdministeredDose(): boolean {
    const { amountAdministered, justification } = this.formApi.getRecord();
    const { doseQuantity } = this.context.getBulkData();

    if (String(amountAdministered) !== String(doseQuantity) && isEmptyOrBlankString(justification as string)) {
      this.showToast('text.alert-change-volume-administered', ToastLevel.WARNING, ToastType.BUBBLE);
      return false;
    }
    return true;
  }

  private changeDeckReadState(read: boolean): void {
    const deckData = this.deckOfCardsApi.getData()
      .map(item => ({
        ...item,
        read
      }));
    this.deckOfCardsApi.setData(deckData);
    this.context.set({ barcodeValid: read })
    this.formApi.validate();
  }

  private showToast(message: string, toastLevel: ToastLevel, type: ToastType): void {
    const toastId = FragmentNames.MILK_AND_DERIVATIVES_EXECUTE;
    ToastService.close(toastId);
    ToastService.open({
      dismissible: true,
      id: toastId,
      level: toastLevel,
      message: MessageService.translate(message),
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type,
    });
  }

  private redirectUserToTimeline(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const redirectRoute = ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION;
    applicationContext.redirect(redirectRoute, { encounterId, patientId });
    applicationContext.clearNavigationStack();
    ModalService.close(FragmentNames.MILK_AND_DERIVATIVES_EXECUTE);
  }

  private isLoading(state: boolean): void {
    this.formApi.setLoading(state);
    this.executeButtonApi.setEnabled(!state);
  }
}

export default MilkAndDerivativesExecuteController;
